/* ==========================================================================
   Speed Junkies HQ - Media / video gallery page

   Three jobs, in order of how much they matter to page weight:

   1. CLICK-TO-PLAY FACADE. No YouTube iframe exists in the document on load.
      Each card shows a static thumbnail; the iframe is created only when the
      visitor clicks play. A live iframe costs roughly 700KB-1MB and 25+
      requests, so 15 of them on load would have made this page heavier than
      the rest of the site combined. This is why the page can carry this many
      videos at all.

   2. INSTAGRAM, LAZILY AND ONCE. Instagram's embed.js is requested a single
      time for the whole page, and only when the first Instagram card comes
      near the viewport. Blockquotes are injected per card on the same
      trigger. A post from a private account, a deleted post, or a blocked
      script all end in the same place: the placeholder stays visible with a
      working "View on Instagram" link, never a blank hole in the grid.

   3. FILTER TABS. Plain hidden-attribute filtering, no layout library.

   Thumbnail note: YouTube reports Shorts as 16:9 through oEmbed, and
   maxresdefault.jpg for a Short is a centre crop of the vertical frame. The
   true 1080x1920 still lives at oar2.jpg for most Shorts, so vertical cards
   try that first and fall back to the crop, which the CSS then fits with
   object-fit:cover. Either way there are no letterbox bars.
   ========================================================================== */
(function () {
  'use strict';

  var IG_SCRIPT = 'https://www.instagram.com/embed.js';
  var igScriptRequested = false;

  /* ---------------------------------------------------------------
     Thumbnails
     --------------------------------------------------------------- */

  function thumbCandidates(id, vertical) {
    var base = 'https://i.ytimg.com/vi/' + encodeURIComponent(id) + '/';
    if (vertical) {
      /* oar2 is the true 1080x1920 frame when YouTube has one. */
      return [base + 'oar2.jpg', base + 'maxresdefault.jpg', base + 'hqdefault.jpg'];
    }
    return [base + 'maxresdefault.jpg', base + 'hqdefault.jpg'];
  }

  /* YouTube serves a 120x90 grey placeholder rather than a 404 for a missing
     thumbnail, so a load event alone does not mean success - check the size. */
  function loadThumb(img, list, index) {
    if (index >= list.length) return;
    var probe = new Image();
    probe.onload = function () {
      if (probe.naturalWidth <= 121 && index < list.length - 1) {
        loadThumb(img, list, index + 1);
        return;
      }
      img.src = probe.src;
    };
    probe.onerror = function () { loadThumb(img, list, index + 1); };
    probe.src = list[index];
  }

  function mountThumb(img) {
    if (img.getAttribute('data-sjm-thumb-done')) return;
    img.setAttribute('data-sjm-thumb-done', '1');
    var id = img.getAttribute('data-sjm-thumb');
    var vertical = img.getAttribute('data-sjm-vertical') === 'true';
    loadThumb(img, thumbCandidates(id, vertical), 0);
  }

  /* Thumbnails are fetched on approach, not on load. The <img> ships with no
     src at all, so an 18-card page costs zero thumbnail requests until the
     visitor scrolls - loading="lazy" alone would not have helped, because
     setting .src from JS on every card defeats it. The true vertical frame
     (oar2.jpg) is around 145KB, so this is the difference between ~350KB and
     ~1.8MB for someone who only looks at the first row. */
  function initThumbs(root) {
    var imgs = root.querySelectorAll('[data-sjm-thumb]:not([data-sjm-thumb-done]):not([data-sjm-thumb-watched])');
    if (!imgs.length) return;

    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(imgs, mountThumb);
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        mountThumb(entry.target);
      });
    }, { rootMargin: '600px 0px' });

    Array.prototype.forEach.call(imgs, function (img) {
      img.setAttribute('data-sjm-thumb-watched', '1');
      io.observe(img);
    });
  }

  /* ---------------------------------------------------------------
     Click-to-play
     --------------------------------------------------------------- */

  function play(button) {
    var frame = button.closest('.sj-m-card__frame');
    if (!frame) return;
    var id = button.getAttribute('data-sjm-play');
    if (!id) return;

    var iframe = document.createElement('iframe');
    /* youtube-nocookie keeps the privacy footprint down; autoplay is correct
       here because the visitor just clicked play. */
    iframe.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) +
      '?autoplay=1&rel=0&modestbranding=1&playsinline=1';
    iframe.title = button.getAttribute('data-sjm-title') || 'YouTube video player';
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    iframe.allowFullscreen = true;

    frame.innerHTML = '';
    frame.appendChild(iframe);
  }

  /* ---------------------------------------------------------------
     Instagram
     --------------------------------------------------------------- */

  function loadIgScript() {
    if (igScriptRequested) return;
    igScriptRequested = true;
    var s = document.createElement('script');
    s.src = IG_SCRIPT;
    s.async = true;
    s.defer = true;
    s.onload = processIg;
    /* No onerror handling needed beyond leaving the placeholders alone -
       that is already the correct fallback. */
    document.head.appendChild(s);
  }

  function processIg() {
    if (window.instgrm && window.instgrm.Embeds && window.instgrm.Embeds.process) {
      try { window.instgrm.Embeds.process(); } catch (e) { /* leave placeholders */ }
    }
  }

  function mountIg(card) {
    if (card.getAttribute('data-sjm-ig-mounted')) return;
    card.setAttribute('data-sjm-ig-mounted', '1');

    var holder = card.querySelector('[data-sjm-ig-holder]');
    var permalink = card.getAttribute('data-sjm-ig');
    if (!holder || !permalink) return;

    var bq = document.createElement('blockquote');
    bq.className = 'instagram-media';
    bq.setAttribute('data-instgrm-permalink', permalink);
    bq.setAttribute('data-instgrm-version', '14');
    bq.style.cssText = 'background:#0e1319;border:0;margin:0 auto;max-width:400px;min-width:0;padding:0;width:100%';
    holder.appendChild(bq);

    loadIgScript();
    processIg();

    /* If Instagram never turns the blockquote into an iframe - a private
       account, a removed post, a blocked script, an ad blocker - the card
       would otherwise render as an empty box. Put the placeholder back and
       mark the card so it can be reported. */
    window.setTimeout(function () {
      if (!holder.querySelector('iframe')) {
        var ph = card.querySelector('[data-sjm-ig-ph]');
        if (ph) ph.hidden = false;
        holder.hidden = true;
        card.setAttribute('data-sjm-ig-failed', '1');
      }
    }, 8000);
  }

  function observeIg(root) {
    var cards = root.querySelectorAll('[data-sjm-ig]:not([data-sjm-ig-observed])');
    if (!cards.length) return;

    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(cards, function (c) {
        c.setAttribute('data-sjm-ig-observed', '1');
        mountIg(c);
      });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        mountIg(entry.target);
      });
    }, { rootMargin: '400px 0px' });

    Array.prototype.forEach.call(cards, function (c) {
      c.setAttribute('data-sjm-ig-observed', '1');
      io.observe(c);
    });
  }

  /* ---------------------------------------------------------------
     Filters
     --------------------------------------------------------------- */

  function applyFilter(section, value) {
    var cards = section.querySelectorAll('[data-sjm-cat]');
    var shown = 0;

    Array.prototype.forEach.call(cards, function (card) {
      var match = value === 'all' || card.getAttribute('data-sjm-cat') === value;
      card.hidden = !match;
      if (match) shown += 1;
    });

    Array.prototype.forEach.call(section.querySelectorAll('[data-sjm-tab]'), function (tab) {
      var on = tab.getAttribute('data-sjm-tab') === value;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    var count = section.querySelector('[data-sjm-count]');
    if (count) {
      var one = count.getAttribute('data-singular') || 'video';
      var many = count.getAttribute('data-plural') || 'videos';
      count.textContent = shown + ' ' + (shown === 1 ? one : many);
    }

    var empty = section.querySelector('[data-sjm-empty]');
    if (empty) empty.hidden = shown !== 0;

    /* Filtering moves cards up the page, so anything that is now on screen
       needs its thumbnail and its Instagram embed. Both are no-ops for
       cards already handled. */
    initThumbs(section);
    observeIg(section);
  }

  /* ---------------------------------------------------------------
     Boot
     --------------------------------------------------------------- */

  function initSection(section) {
    if (section.getAttribute('data-sjm-ready')) {
      initThumbs(section);
      observeIg(section);
      return;
    }
    section.setAttribute('data-sjm-ready', '1');

    initThumbs(section);
    observeIg(section);

    section.addEventListener('click', function (event) {
      var playBtn = event.target.closest ? event.target.closest('[data-sjm-play]') : null;
      if (playBtn && section.contains(playBtn)) {
        event.preventDefault();
        play(playBtn);
        return;
      }
      var tab = event.target.closest ? event.target.closest('[data-sjm-tab]') : null;
      if (tab && section.contains(tab)) {
        event.preventDefault();
        applyFilter(section, tab.getAttribute('data-sjm-tab'));
      }
    });

    /* Left/right arrows move between tabs, per the ARIA tablist pattern. */
    section.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      var tab = event.target.closest ? event.target.closest('[data-sjm-tab]') : null;
      if (!tab) return;
      var tabs = Array.prototype.slice.call(section.querySelectorAll('[data-sjm-tab]'));
      var i = tabs.indexOf(tab);
      if (i < 0) return;
      var next = tabs[(i + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
      next.focus();
      applyFilter(section, next.getAttribute('data-sjm-tab'));
      event.preventDefault();
    });

    applyFilter(section, 'all');
  }

  function boot(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-sjm-section]'), initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); }, { once: true });
  } else {
    boot(document);
  }

  /* Shopify theme editor re-renders the section on every setting change. */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target || document); });
})();
